import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  RestaurantAddress,
  RestaurantLocation,
  TTRestaurantProfile,
} from '../../../interfaces/restaurant';
import { useSelector } from 'react-redux';
import { RootState } from '../../../rootReducer';
import { TTOrderV2 } from '../../../interfaces/order';
import { TTUserProfile } from '../../../interfaces/user';
import PillLabel from '../../../components/PillLabel';
import { fetchOrderRestaurants } from '../../../features/order/order';
import { useAppDispatch } from '../../../store';
import {
  BuildingStorefrontIcon,
  CalendarIcon,
  ChatBubbleBottomCenterTextIcon,
  InformationCircleIcon,
  UserIcon,
} from '@heroicons/react/20/solid';

type Props = {
  classNameName?: string;
  pickup_time: string;
  location?: RestaurantLocation;
};

export const PickUpOrderInfo: React.FC<Props> = ({ pickup_time, location }) => {
  const order = useSelector<RootState, TTOrderV2 | null>(
    state => state.order.order,
  );

  const guest = useSelector<RootState, TTUserProfile | null>(
    state => state.auth.userProfile,
  );

  const restaurant_profiles = useSelector<RootState, TTRestaurantProfile[]>(
    state => state.order.order_restaurants,
  );
  const restaurant_addresses = useSelector<RootState, RestaurantAddress[]>(
    state => state.order.order_restaurant_addresses,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (order !== null) {
      dispatch(fetchOrderRestaurants(order));
    }
  }, [order]);

  return (
    <>
      {order?.order_tabs
        ?.map(tab => tab.order_fulfillments)
        ?.reduce((a, b) => a.concat(b), [])
        ?.filter(fulfillment => fulfillment.guest_id === guest?.id)
        ?.map(fulfillment => {
          return (
            <div
              className="flex flex-col rounded-md bg-secondary-container shadow-md mx-6 my-4 px-2 py-1 space-y-2"
              key={fulfillment?.id?.toString()}
            >
              <div className="flex flex-col  ">
                <div className="flex space-x-1 items-center">
                  <BuildingStorefrontIcon className="w-4 h-4 text-primary" />

                  <div className="text-sm text-primary">Vendor Name</div>
                </div>
                <div className="text-md text-on-secondary-container font-semibold">
                  {restaurant_profiles?.filter(
                    r =>
                      r.restaurant_id ===
                      order?.order_tabs?.filter(
                        t => t.id === fulfillment?.order_tab_id,
                      )[0]?.restaurant_id,
                  )[0]?.name ?? ''}
                </div>
              </div>
              <div className="h-0.5 w-full bg-outline mx-auto" />
              {restaurant_profiles?.filter(
                r =>
                  r.restaurant_id ===
                  order?.order_tabs?.filter(
                    t => t.id === fulfillment?.order_tab_id,
                  )[0]?.restaurant_id,
              )[0]?.config?.simple_pickup ? (
                <></>
              ) : (
                <>
                  <div className="flex flex-col  ">
                    <div className="flex space-x-1 items-center">
                      <BuildingStorefrontIcon className="w-4 h-4 text-primary" />

                      <div className="text-sm text-primary">
                        Pickup Location
                      </div>
                    </div>
                    <div className="text-md text-on-secondary-container font-semibold">
                      {restaurant_addresses?.filter(
                        r =>
                          r.restaurant_id ===
                          order?.order_tabs?.filter(
                            t => t.id === fulfillment?.order_tab_id,
                          )[0]?.restaurant_id,
                      )[0]?.address_line_1 +
                        ', ' +
                        restaurant_addresses?.filter(
                          r =>
                            r.restaurant_id ===
                            order?.order_tabs?.filter(
                              t => t.id === fulfillment?.order_tab_id,
                            )[0]?.restaurant_id,
                        )[0]?.city +
                        ', ' +
                        restaurant_addresses?.filter(
                          r =>
                            r.restaurant_id ===
                            order?.order_tabs?.filter(
                              t => t.id === fulfillment?.order_tab_id,
                            )[0]?.restaurant_id,
                        )[0]?.administrative_area_level_1 +
                        ', ' +
                        restaurant_addresses?.filter(
                          r =>
                            r.restaurant_id ===
                            order?.order_tabs?.filter(
                              t => t.id === fulfillment?.order_tab_id,
                            )[0]?.restaurant_id,
                        )[0]?.zip_postal ?? ''}
                    </div>
                  </div>
                  <div className="h-0.5 w-full bg-outline mx-auto" />

                  <div className="flex flex-col   ">
                    <div className="flex space-x-1 items-center">
                      <InformationCircleIcon className="w-4 h-4 text-primary" />

                      <div className="text-sm text-primary">Status</div>
                    </div>

                    <div className="text-md text-gray-900 font-semibold">
                      {fulfillment?.state === 'pending' ? (
                        <PillLabel
                          label="Pending"
                          color="bg-indigo-700"
                          label_size="text-md"
                          text_color="text-indigo-200"
                        />
                      ) : fulfillment?.state === 'accepted' ? (
                        <PillLabel
                          label="Accepted"
                          color="bg-green-700"
                          label_size="text-md"
                          text_color="text-green-200"
                        />
                      ) : fulfillment?.state === 'ready' ? (
                        <PillLabel
                          label="Ready"
                          color="bg-green-700"
                          label_size="text-md"
                          text_color="text-green-200"
                        />
                      ) : fulfillment?.state === 'completed' ? (
                        <PillLabel
                          label="Completed"
                          color="bg-green-700"
                          label_size="text-md"
                          text_color="text-green-200"
                        />
                      ) : (
                        <PillLabel
                          label="Canceled"
                          color="bg-inverse-surface"
                          label_size="text-md"
                          text_color="text-on-inverse-surface"
                        />
                      )}
                    </div>
                  </div>
                  <div className="h-0.5 w-full bg-outline mx-auto" />
                </>
              )}

              <div className="flex flex-col  ">
                <div className="flex space-x-1 items-center">
                  <UserIcon className="w-4 h-4 text-primary" />

                  <div className="text-sm text-primary">First & Last Name</div>
                </div>
                <div className="text-md text-on-secondary-container font-semibold">
                  {guest?.first_name + (' ' + (guest?.last_name ?? ''))}
                </div>
              </div>
              <div className="h-0.5 w-full bg-outline mx-auto" />

              <div className="flex flex-col  ">
                <div className="flex space-x-1 items-center">
                  <CalendarIcon className="w-4 h-4 text-primary" />

                  <div className="text-sm text-primary">Order Placed at</div>
                </div>
                <time className="text-md text-on-secondary-container font-semibold">
                  {moment
                    .utc(fulfillment?.created_at)
                    .local()
                    .format('MM/DD/YYYY, h:mm a')}
                </time>
              </div>
              <div className="h-0.5 w-full bg-outline mx-auto" />
              {fulfillment?.pickup_details?.scheduled_for ? (
                <div className="flex flex-col  ">
                  <div className="flex space-x-1 items-center">
                    <CalendarIcon className="w-4 h-4 text-primary" />

                    <div className="text-sm text-primary">
                      Order Scheduled for
                    </div>
                  </div>
                  <time className="text-md text-on-secondary-container font-semibold">
                    {moment
                      .utc(fulfillment?.pickup_details?.scheduled_for)
                      .local()
                      .format('MM/DD/YYYY, h:mm a')}
                  </time>
                </div>
              ) : (
                <></>
              )}
              <div className="h-0.5 w-full bg-outline mx-auto" />
              <div className="flex flex-col  ">
                <div className="flex space-x-1 items-center">
                  <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-primary" />

                  <div className="text-sm text-primary">Comment</div>
                </div>
                <p className="text-md text-on-secondary-container mb-0 font-semibold">
                  {fulfillment?.pickup_details?.note ?? ''}
                </p>
              </div>
            </div>
          );
        })}
    </>
  );
};
